import { useCallback, useEffect, useMemo, useState } from "react";
import { NavigationItem } from "../config/navigation";

const STORAGE_VERSION = 1;
const STORAGE_KEY_PREFIX = `argus-nav-favorites:v${STORAGE_VERSION}`;

const DEFAULT_FAVORITE_PRIORITY = [
  "dashboard",
  "player-data",
  "chat-logs",
  "status-monitor",
  "economy",
];

const dedupeIds = (ids: string[]) => [...new Set(ids)];

const getStorageKey = (userUuid?: string | null) =>
  `${STORAGE_KEY_PREFIX}:${userUuid || "anonymous"}`;

const buildDefaultFavorites = (visibleIds: Set<string>) =>
  DEFAULT_FAVORITE_PRIORITY.filter((id) => visibleIds.has(id));

interface StoredFavoritesShape {
  favoriteIds: string[];
}

const parseStoredFavorites = (storedValue: string | null) => {
  if (!storedValue) {
    return null;
  }
  try {
    const parsed = JSON.parse(storedValue) as StoredFavoritesShape;
    if (!Array.isArray(parsed.favoriteIds)) {
      return null;
    }
    return dedupeIds(parsed.favoriteIds);
  } catch (error) {
    console.error("Failed to parse saved navigation favourites", error);
    return null;
  }
};

const normalizeFavorites = (
  ids: string[],
  visibleIds: Set<string>,
  fallbackIds: string[]
) => {
  const validIds = dedupeIds(ids).filter((id) => visibleIds.has(id));
  if (validIds.length > 0) {
    return validIds;
  }
  return fallbackIds;
};

interface UseNavFavoritesParams {
  visibleItems: NavigationItem[];
  userUuid?: string | null;
}

export const useNavFavorites = ({
  visibleItems,
  userUuid,
}: UseNavFavoritesParams) => {
  const visibleItemById = useMemo(
    () =>
      new Map<string, NavigationItem>(
        visibleItems.map((item) => [item.id, item])
      ),
    [visibleItems]
  );
  const visibleIdSet = useMemo(
    () => new Set(visibleItems.map((item) => item.id)),
    [visibleItems]
  );

  const storageKey = useMemo(() => getStorageKey(userUuid), [userUuid]);

  const [favoriteIds, setFavoriteIds] = useState<string[]>(() => {
    const defaults = buildDefaultFavorites(visibleIdSet);
    const parsedStored = parseStoredFavorites(localStorage.getItem(storageKey));
    if (!parsedStored) {
      return defaults;
    }
    return normalizeFavorites(parsedStored, visibleIdSet, defaults);
  });

  useEffect(() => {
    const defaults = buildDefaultFavorites(visibleIdSet);
    const parsedStored = parseStoredFavorites(localStorage.getItem(storageKey));
    if (!parsedStored) {
      setFavoriteIds(defaults);
      return;
    }
    setFavoriteIds(normalizeFavorites(parsedStored, visibleIdSet, defaults));
  }, [storageKey, visibleIdSet]);

  useEffect(() => {
    try {
      const payload: StoredFavoritesShape = { favoriteIds };
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (error) {
      console.error("Failed to save navigation favourites", error);
    }
  }, [favoriteIds, storageKey]);

  const favoriteItems = useMemo(
    () =>
      favoriteIds
        .map((id) => visibleItemById.get(id))
        .filter((item): item is NavigationItem => Boolean(item)),
    [favoriteIds, visibleItemById]
  );

  const isFavorite = useCallback(
    (itemId: string) => favoriteIds.includes(itemId),
    [favoriteIds]
  );

  const toggleFavorite = useCallback(
    (itemId: string) => {
      if (!visibleIdSet.has(itemId)) {
        return;
      }
      setFavoriteIds((prev) =>
        prev.includes(itemId)
          ? prev.filter((id) => id !== itemId)
          : [...prev, itemId]
      );
    },
    [visibleIdSet]
  );

  const reorderFavorites = useCallback(
    (nextOrderedIds: string[]) => {
      setFavoriteIds((prev) => {
        const currentVisibleFavorites = prev.filter((id) => visibleIdSet.has(id));
        const ordered = dedupeIds(nextOrderedIds).filter((id) =>
          currentVisibleFavorites.includes(id)
        );
        const missing = currentVisibleFavorites.filter(
          (id) => !ordered.includes(id)
        );
        return [...ordered, ...missing];
      });
    },
    [visibleIdSet]
  );

  return {
    favoriteIds,
    favoriteItems,
    isFavorite,
    toggleFavorite,
    reorderFavorites,
  };
};
